import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import {
  SpecialTextField,
  SectionHeader,
  TableAlerts,
  Tooltip,
} from "../../../../../app/components";
import { useStudyPlan } from "../../../../../app/contexts/study.context";
import SeparateContainer from "../../../../../app/components/separate.Container";
import SideBySide from "../../../../../app/components/sideBySide";
import { usePauseValidation } from "../../../../../app/hooks/useStudyPlanValidations";
import { getSitesAndSubjectsParameters, isEmpty } from "../../../utils/helpers";
import { formatInputValue } from "../../../../../app/helpers/utils";
import { toParticipantUiText } from "../../../../../app/utils/participantTerminology";
import { ActivatedSitesStats } from "../../../activated.sites.stats";
import { useActivatedSitesLiveTotal } from "../../../../../app/hooks/useActivatedSitesLiveTotal";

const ScenarioA = ({ values, setValues, setFieldErrors }) => {
  const { studyPlan, updatePlan, siteActivationStats } = useStudyPlan();
  const {
    enrollmentRate,
    screenFailRate,
    dropOutRate,
    screeningPeriodDuration,
    treatmentDuration,
    followUpDuration,
    parameters,
    milestones,
    pauseEnrollmentAfterPatient = null,
    pauseEnrollmentDays = null,
    cohortType,
    studyPlanTypeDetail,
  } = studyPlan;
  const [sites_to_activate, subjects_to_screen] = getSitesAndSubjectsParameters(studyPlan?.parameters);
  const [
    number_patients_enrolled,
    number_patients_treatment,
    number_patients_follow_up,
  ] = milestones.filter(
    ({ milestoneId }) =>
      milestoneId === 144 || milestoneId === 147 || milestoneId === 150,
  );
  const [PauseEnrollment, setPauseEnrollment] = useState({
    pauseEnrollmentAfterPatient: pauseEnrollmentAfterPatient,
    pauseEnrollmentDays: pauseEnrollmentDays,
  });
  const [afterShowEmptyError, setAfterShowEmptyError] = useState(false);
  const [daysShowEmptyError, setDaysShowEmptyError] = useState(false);

  const isNotSequentialCohort = useMemo(
    () => cohortType !== 2 && studyPlanTypeDetail !== "Cohort (D)",
    [cohortType, studyPlanTypeDetail],
  );

  const isRequired = (lowerBound, rate) => isEmpty(lowerBound) && isEmpty(rate);

  const isNumberPatientsEnrolledOrScreenFaiLRateRequired = isRequired(
    number_patients_enrolled.lowerBound,
    screenFailRate,
  );

  const isPatientsCompletingTreatmentOrDropOutRateRequired = isRequired(
    number_patients_treatment.lowerBound,
    dropOutRate,
  );

  const handlePauseEnrollmentChange = (field, value) => {
    const formattedValue = formatInputValue(value);

    setPauseEnrollment({
      ...PauseEnrollment,
      [field]: formattedValue,
    });

    if (field === "pauseEnrollmentDays") {
      updatePlan("pauseEnrollmentDays", formattedValue);
    }

    if (field === "pauseEnrollmentAfterPatient") {
      updatePlan("pauseEnrollmentAfterPatient", formattedValue);
    }

    setValues({
      ...values,
      ...studyPlan,
      [field]: formattedValue,
      updated: new Date().getTime(),
    });
  };

  const handleFieldChange = (type, field, key, value) => {
    const formattedValue = formatInputValue(value);
    const isParameter = type === "parameters";
    const array = isParameter ? parameters : milestones;
    const keyName = isParameter ? "parameterName" : "milestoneName";
    
    const index = array.map((item) => item[keyName]).indexOf(key);
    const newArray = [...array];
    newArray[index][field] = formattedValue;

    updatePlan(type, newArray);
    setValues({ 
      ...values, 
      [type]: newArray,
      updated: new Date().getTime() 
    });
  };

  const defaultSpecialTextFieldProps = {
    showArrows: false,
  };

  const customValidationDefinition = useMemo(
    () => ({
      enrollmentRate: {
        range: [0.0, 100.0],
        validateEmpty: false,
        checkIfInt: false,
        checkIfDec: false,
      },
      numberofactivatedsites: {
        range: [1, Infinity],
        validateEmpty: true,
        checkIfInt: true,
        checkIfDec: false,
      },
      numberofpatientsscreened: {
        range: [1, Infinity],
        validateEmpty: true,
        checkIfInt: true,
        checkIfDec: false,
      },
      "screeningperiodperpatient(days)": {
        range: [0, Infinity],
        validateEmpty: true,
        checkIfInt: true,
        checkIfDec: false,
      },
      "treatmentperiodperpatient(days)": {
        range: [0, Infinity],
        validateEmpty: true,
        checkIfInt: true,
        checkIfDec: false,
      },
      "follow-upperiodperpatient(days)": {
        range: [0, Infinity],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
      "screenfailrate(indecimals)": {
        range: [0.0, 0.99],
        validateEmpty: false,
        checkIfInt: false,
        checkIfDec: true,
      },
      "dropoutrate(indecimals)": {
        range: [0.0, 0.99],
        validateEmpty: false,
        checkIfInt: false,
        checkIfDec: true,
      },
      numberofpatientsenrolled: {
        range: [1, subjects_to_screen.lowerBound],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
      numbrofpatientscompletingtreatment: {
        range: [
          1,
          number_patients_enrolled.lowerBound || subjects_to_screen.lowerBound,
        ],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
      numbrofpatientscompletingfollowup: {
        range: [
          1,
          number_patients_treatment.lowerBound ||
            number_patients_enrolled.lowerBound ||
            subjects_to_screen.lowerBound,
        ],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
      pauseEnrollment: {
        range: [1, 9999],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
    }),
    [
      subjects_to_screen.lowerBound,
      number_patients_enrolled.lowerBound,
      number_patients_treatment.lowerBound,
    ],
  );

  useEffect(() => {
    const { pauseEnrollmentDays, pauseEnrollmentAfterPatient } =
      PauseEnrollment;

    const isDaysEmpty = !pauseEnrollmentDays;
    const isAfterPatientEmpty = !pauseEnrollmentAfterPatient;

    const pauseEnrollmentAfterShouldShowEmptyError =
      pauseEnrollmentAfterPatient &&
      (pauseEnrollmentDays === "" || isDaysEmpty);

    const pauseEnrollmentDaysShouldDisablePauseEnrollment =
      pauseEnrollmentDays &&
      (pauseEnrollmentAfterPatient === "" || isAfterPatientEmpty);

    setAfterShowEmptyError(pauseEnrollmentDaysShouldDisablePauseEnrollment);
    setDaysShowEmptyError(pauseEnrollmentAfterShouldShowEmptyError);

    if (isDaysEmpty && isAfterPatientEmpty) {
      setFieldErrors((prevState) => {
        const { undefined: _, pauseEnrollmentError, ...rest } = prevState;
        return rest;
      });
    } else if (isDaysEmpty || isAfterPatientEmpty) {
      setFieldErrors((prevState) => ({
        ...prevState,
        pauseEnrollmentError: {
          ...prevState.pauseEnrollmentError,
          errors: ["Can't be empty."],
        },
      }));
    } else {
      setFieldErrors((prevState) => {
        const { undefined: _, pauseEnrollmentError, ...rest } = prevState;
        return rest;
      });
    }
  }, [
    PauseEnrollment.pauseEnrollmentDays,
    PauseEnrollment.pauseEnrollmentAfterPatient,
    PauseEnrollment,
    setFieldErrors,
  ]);

  useEffect(() => {
    if (isNumberPatientsEnrolledOrScreenFaiLRateRequired) {
      setFieldErrors((prevState) => ({
        ...prevState,
        requiredError: {
          ...prevState.requiredError,
          errors: ["Can't be empty."],
        },
      }));
    } else {
      setFieldErrors((prevState) => {
        const { requiredError, ...rest } = prevState;
        return rest;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenFailRate, number_patients_enrolled.lowerBound]);

  useEffect(() => {
    if (isPatientsCompletingTreatmentOrDropOutRateRequired) {
      setFieldErrors((prevState) => ({
        ...prevState,
        dropTreatmentRequired: {
          ...prevState.dropTreatmentRequired,
          errors: ["Can't be empty."],
        },
      }));
    } else {
      setFieldErrors((prevState) => {
        const { dropTreatmentRequired, ...rest } = prevState;
        return rest;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropOutRate, number_patients_treatment.lowerBound]);

  const pauseAlerts = usePauseValidation(
    number_patients_enrolled.lowerBound,
    PauseEnrollment,
    screenFailRate,
    subjects_to_screen.lowerBound,
  );
  
  const liveAllCountriesSites = useActivatedSitesLiveTotal(
    studyPlan.studyPlanType,
    0,
    0,
    0,
    0,
    Number(siteActivationStats?.sitesActivatedLowerBound) || 0,
    Number(siteActivationStats?.sitesActivatedUpperBound) || 0,
    Number(siteActivationStats?.totalSitesRequired) || 0,
  );

  return (
    <Container>
      <TwoColumnLayout>
        <FlexCol>
          <SeparateContainer>
            <SideBySide>
              <SectionHeader
                hasStartAdornment={false}
                marginBottom={false}
                paddingBottom={false}
              >
                Overall Parameters
              </SectionHeader>
            </SideBySide>
            <div style={{ padding: "20px" }}>
              <SpecialTextField
                {...defaultSpecialTextFieldProps}
                style={{ marginLeft: "0.5em", marginBlock: "0.5em" }}
                label="Enrollment Rate (p/s/m)"
                value={enrollmentRate}
                onChange={(value) => {
                  updatePlan("enrollmentRate", value);
                }}
                customValidation={customValidationDefinition["enrollmentRate"]}
                endadornment={
                  <Tooltip info="Only complete if you are expecting the same enrollment rate across all countries (if not, skip here and complete at the country level)." />
                }
                setFieldErrors={setFieldErrors}
              />
              <div
                style={{
                  display: "flex",
                  gap: "1em",
                  marginLeft: "0.5em",
                  marginBlock: "0.5em",
                }}
              >
                <SpecialTextField
                  {...defaultSpecialTextFieldProps}
                  label={sites_to_activate.parameterDescr}
                  value={sites_to_activate.lowerBound}
                  customValidation={
                    customValidationDefinition["numberofactivatedsites"]
                  }
                  onChange={(value) => {
                    handleFieldChange(
                      "parameters",
                      "lowerBound",
                      sites_to_activate.parameterName,
                      value,
                    );
                  }}
                  required
                  key={sites_to_activate.parameterName + 2}
                  endadornment={
                    <Tooltip
                      info={PARAMETER_INFO[sites_to_activate.parameterName]}
                    />
                  }
                  setFieldErrors={setFieldErrors}>
                    <ActivatedSitesStats overrides={liveAllCountriesSites} studyPlanType={studyPlan.studyPlanType} />
                  </SpecialTextField>
              </div>
            </div>
          </SeparateContainer>

          <SeparateContainer>
            <div style={{ marginBottom: "1em" }}>
              <SideBySide>
                <SectionHeader
                  hasStartAdornment={false}
                  marginBottom={false}
                  paddingBottom={false}
                >
                  Per Participant Duration from SOE
                </SectionHeader>
              </SideBySide>
            </div>
            <div style={{ padding: "20px" }}>
              <SpecialTextField
                {...defaultSpecialTextFieldProps}
                style={{ marginLeft: "0.5em", marginBlock: "0.5em" }}
                label="Screening Period Per Participant (days)"
                value={screeningPeriodDuration}
                customValidation={
                  customValidationDefinition["screeningperiodperpatient(days)"]
                }
                onChange={(value) =>
                  updatePlan("screeningPeriodDuration", value)
                }
                required
                setFieldErrors={setFieldErrors}
              />
              <SpecialTextField
                {...defaultSpecialTextFieldProps}
                style={{ marginLeft: "0.5em", marginBlock: "0.5em" }}
                label="Treatment Period Per Participant (days)"
                value={treatmentDuration}
                customValidation={
                  customValidationDefinition["treatmentperiodperpatient(days)"]
                }
                onChange={(value) => updatePlan("treatmentDuration", value)}
                required
                setFieldErrors={setFieldErrors}
              />
              <SpecialTextField
                {...defaultSpecialTextFieldProps}
                style={{ marginLeft: "0.5em", marginBlock: "0.5em" }}
                label="Follow-Up Period Per Participant (days)"
                value={followUpDuration}
                customValidation={
                  customValidationDefinition["follow-upperiodperpatient(days)"]
                }
                onChange={(value) => updatePlan("followUpDuration", value)}
                endadornment={<Tooltip info="If applicable to the protocol." />}
                setFieldErrors={setFieldErrors}
              />
            </div>
          </SeparateContainer>
          {isNotSequentialCohort && studyPlanTypeDetail !== "Remodel (R)" && (
            <SeparateContainer>
              <div>
                <SideBySide>
                  <SectionHeader
                    hasStartAdornment={false}
                    marginBottom={false}
                    paddingBottom={false}
                    endAdornment={
                      <Tooltip info="Complete only if a pause in enrollment is expected. For example, if an Interim Analysis is needed after the first 100 participants completes 3 months of treatment before proceeding, include 100 in the first field and then calculate when screening would resume in the second. Consider the treatment time for the 100th participant plus time for the associated data entry and completing the interim analysis. REMINDER: the second field is when participant screening (NOT enrollment) resumes." />
                    }
                  >
                    Screening/Enrollment Pause (if applicable)
                  </SectionHeader>
                </SideBySide>
              </div>
              <TableAlerts alerts={pauseAlerts} />
              <div style={{ padding: "20px" }}>
                <div style={{ padding: "0.5em" }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    label="Pause Enrollment After Participant #"
                    value={PauseEnrollment.pauseEnrollmentAfterPatient}
                    showCustomError={afterShowEmptyError}
                    customMessageError={"Can't be empty."}
                    required={!isEmpty(PauseEnrollment.pauseEnrollmentDays)}
                    showTooltip={
                      PauseEnrollment.pauseEnrollmentDays ? true : false
                    }
                    showArrows={false}
                    customValidation={
                      customValidationDefinition["pauseEnrollment"]
                    }
                    onChange={(value) => {
                      handlePauseEnrollmentChange(
                        "pauseEnrollmentAfterPatient",
                        value,
                      );
                    }}
                  />
                </div>
                <div style={{ padding: "0.5em" }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    label="Resume Screening After (days)"
                    showCustomError={daysShowEmptyError}
                    customMessageError={"Can't be empty."}
                    value={PauseEnrollment.pauseEnrollmentDays}
                    showTooltip={
                      PauseEnrollment.pauseEnrollmentAfterPatient ? true : false
                    }
                    showArrows={false}
                    required={!isEmpty(PauseEnrollment.pauseEnrollmentAfterPatient)}
                    customValidation={
                      customValidationDefinition["pauseEnrollment"]
                    }
                    onChange={(value) => {
                      handlePauseEnrollmentChange("pauseEnrollmentDays", value);
                    }}
                  />
                </div>
              </div>
            </SeparateContainer>
          )}
        </FlexCol>
        <FlexCol>
          <SeparateContainer>
            <div style={{ marginBottom: "1em" }}>
              <SideBySide>
                <SectionHeader
                  hasStartAdornment={false}
                  marginBottom={false}
                  paddingBottom={false}
                >
                  Participant Numbers
                </SectionHeader>
              </SideBySide>
            </div>
            <div style={{ padding: "20px" }}>
              <div
                style={{
                  display: "flex",
                  gap: "1em",
                  marginLeft: "0.5em",
                  marginBlock: "1em",
                }}
              >
                <SpecialTextField
                  {...defaultSpecialTextFieldProps}
                  label={toParticipantUiText(subjects_to_screen.parameterDescr)}
                  value={subjects_to_screen.lowerBound}
                  customValidation={
                    customValidationDefinition["numberofpatientsscreened"]
                  }
                  onChange={(value) => {
                    handleFieldChange(
                      "parameters",
                      "lowerBound",
                      subjects_to_screen.parameterName,
                      value,
                    );
                  }}
                  required
                  key={subjects_to_screen.parameterName + 2}
                  endadornment={
                    <Tooltip
                      zIndex={10000}
                      info={PARAMETER_INFO[subjects_to_screen.parameterName]}
                    />
                  }
                  setFieldErrors={setFieldErrors}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "1em",
                  background: "var(--surface-50)",
                  padding: "0.5em",
                  border: "1px solid var(--surface-50)",
                  borderRadius: "4px",
                  marginBlock: "1em",
                }}
              >
                <div style={{ flex: 1 }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    {...defaultSpecialTextFieldProps}
                    label="Number of Participants Enrolled"
                    value={number_patients_enrolled.lowerBound}
                    disabled={!isEmpty(screenFailRate)}
                    required={isEmpty(screenFailRate)}
                    showCustomError={
                      isNumberPatientsEnrolledOrScreenFaiLRateRequired
                    }
                    customMessageError={"Can't be empty."}
                    customValidation={
                      customValidationDefinition["numberofpatientsenrolled"]
                    }
                    onChange={(value) => handleFieldChange(
                      "milestones",
                      "lowerBound",
                      number_patients_enrolled.milestoneName,
                      value,
                    )}
                    key={number_patients_enrolled.milestoneName}
                  />
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    {...defaultSpecialTextFieldProps}
                    label="Screen Fail Rate (in decimals)"
                    value={screenFailRate}
                    disabled={!isEmpty(number_patients_enrolled.lowerBound)}
                    required={isEmpty(number_patients_enrolled.lowerBound)}
                    customValidation={
                      customValidationDefinition["screenfailrate(indecimals)"]
                    }
                    onChange={(value) => {
                      const formattedValue = formatInputValue(value);
                      updatePlan("screenFailRate", formattedValue)
                      setValues({
                        ...values,
                        ...studyPlan,
                        screenFailRate: formattedValue,
                        updated: new Date().getTime(),
                      });
                    }}
                    showCustomError={
                      isNumberPatientsEnrolledOrScreenFaiLRateRequired
                    }
                    customMessageError={"Can't be empty."}
                    inputProps={{ step: 0.01 }}
                    min={0.01}
                    endadornment={
                      <Tooltip
                        zIndex={10000}
                        info="Either the Screen Fail Rate OR the Number of Participants Enrolled must be completed here. If a certain number of enrolled participants is required in a specific country, also complete this at the country level."
                      />
                    }
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "1em",
                  background: "var(--surface-50)",
                  padding: "0.5em",
                  border: "1px solid var(--surface-50)",
                  borderRadius: "4px",
                  marginBlock: "1em",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    {...defaultSpecialTextFieldProps}
                    key={number_patients_treatment.milestoneId}
                    label={toParticipantUiText(number_patients_treatment.milestoneLabel)}
                    value={number_patients_treatment.lowerBound}
                    customValidation={
                      customValidationDefinition[
                        "numbrofpatientscompletingtreatment"
                      ]
                    }
                    onChange={(value) => handleFieldChange(
                      "milestones",
                      "lowerBound",
                      number_patients_treatment.milestoneName,
                      value,
                    )}
                    disabled={!isEmpty(dropOutRate)}
                    required={isEmpty(dropOutRate)}
                    showArrows={false}
                    showCustomError={
                      isPatientsCompletingTreatmentOrDropOutRateRequired
                    }
                    customMessageError={"Can't be empty."}
                  />
                </div>
                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    {...defaultSpecialTextFieldProps}
                    label="Drop Out Rate (in decimals)"
                    value={dropOutRate}
                    disabled={!isEmpty(number_patients_treatment.lowerBound)}
                    required={isEmpty(number_patients_treatment.lowerBound)}
                    customValidation={
                      customValidationDefinition["dropoutrate(indecimals)"]
                    }
                    onChange={(value) => updatePlan("dropOutRate", value)}
                    showCustomError={
                      isPatientsCompletingTreatmentOrDropOutRateRequired
                    }
                    customMessageError={"Can't be empty."}
                    inputProps={{ step: 0.01 }}
                    min={0.01}
                    endadornment={
                      <Tooltip info="Either the Drop Out Rate OR the Number of Participants Completing Treatment must be completed here. If a certain number of completed participants is required in a specific country, also complete this at the country level." />
                    }
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "1em",
                  marginLeft: "0.5em",
                  marginBlock: "0.5em",
                }}
              >
                <SpecialTextField
                  setFieldErrors={setFieldErrors}
                  {...defaultSpecialTextFieldProps}
                  key={number_patients_follow_up.milestoneId}
                  label={toParticipantUiText(number_patients_follow_up.milestoneLabel)}
                  value={number_patients_follow_up.lowerBound}
                  customValidation={
                    customValidationDefinition[
                      "numbrofpatientscompletingfollowup"
                    ]
                  }
                  onChange={(value) => handleFieldChange(
                    "milestones",
                    "lowerBound",
                    number_patients_follow_up.milestoneName,
                    value,
                  )}
                  endadornment={
                    <Tooltip
                      info={
                        MILESTONE_INFO[number_patients_follow_up.milestoneName]
                      }
                    />
                  }
                />
              </div>
            </div>
          </SeparateContainer>
        </FlexCol>
      </TwoColumnLayout>
    </Container>
  );
};

export default ScenarioA;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 1em;
`;

const PARAMETER_INFO = {
  SITES_TO_ACTIVATE:
    "Complete with the total number of sites across all countries.",
  SUBJECTS_TO_SCREEN:
    "Include the total number of participants to screen [Formula = Number of Enrolled Participants/(1 ─ Screen Failure Rate in decimals)]. The simulation will optimize the distribution of participants across the countries. If a specific distribution is required in a country, enter that value at the country level as well.",
};
const MILESTONE_INFO = {
  NUM_SUBJECTS_ENROLLED:
    "If a certain number of enrolled participants is required in a specific country, also complete this at the country level. Either the Screen Fail Rate OR the Number of Participants Enrolled must be completed here or at the country level.",
  NUM_SUBJECTS_COMPLETED: "If applicable to the protocol.",
};

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 0.5em;
  margin-top: 1em;
`;

const FlexCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
`;
